import {inject, Injectable} from '@angular/core';
import {injectMutation, QueryClient} from '@tanstack/angular-query-experimental';
import {lastValueFrom} from 'rxjs';
import {CreateCreditNoteRequest, CreditNoteService, UpdateCreditNoteRequest} from '../../../../api';
import {QueryKey} from '../../../constants/query-key';

@Injectable({providedIn: 'root'})
export class CreditNoteStore {
  private readonly creditNoteService = inject(CreditNoteService);
  private readonly queryClient = inject(QueryClient);

  createMutation = injectMutation(() => ({
    mutationFn: (request: CreateCreditNoteRequest) =>
      lastValueFrom(this.creditNoteService.createCreditNote(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.CREDIT_NOTES]});
      // Crediting an invoice moves it to CREDITED, so the invoice list must refresh too.
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICES]});
    },
  }));

  updateMutation = injectMutation(() => ({
    mutationFn: ({id, request}: {id: number; request: UpdateCreditNoteRequest}) =>
      lastValueFrom(this.creditNoteService.updateCreditNote(id, request)),
    onSuccess: (_, {id}) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.CREDIT_NOTES]});
      this.queryClient.invalidateQueries({queryKey: ['creditNote', id]});
      this.queryClient.invalidateQueries({queryKey: ['creditNotePdf']});
      this.queryClient.invalidateQueries({queryKey: ['creditNoteAccountingOperations']});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICES]});
    },
  }));

  sendMutation = injectMutation(() => ({
    mutationFn: ({id, params}: {id: number; params: SendCreditNoteParams}) =>
      lastValueFrom(this.creditNoteService.sendCreditNote(
        id, params.to, params.cc ?? [], params.bcc ?? [], params.object, params.content, params.attachments,
      )),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.CREDIT_NOTES]});
    },
  }));

  deleteMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.creditNoteService.deleteCreditNote(id)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.CREDIT_NOTES]});
      // Deleting a draft restores the source invoice state, so refresh the invoice list too.
      this.queryClient.invalidateQueries({queryKey: [QueryKey.INVOICES]});
    },
  }));
}

export interface SendCreditNoteParams {
  to: string[];
  object: string;
  content: string;
  cc?: string[] | null;
  bcc?: string[] | null;
  attachments: File[];
}
